/**
 * BT Subscription widget
 * - Syncs selling_plan into the Horizon product form (does NOT take over ATC)
 * - After a successful subscription add, attaches the free gift + discount
 * - Does not re-add the gift when it is removed from the cart
 *
 * FIXES vs previous version:
 * 1. #applyGiftDiscount now checks the response for `applicable: true` on the
 *    code instead of assuming a 200 OK means the discount actually applied.
 *    Shopify's /cart/update.js can return 200 with the code present but
 *    flagged `applicable: false` (wrong scoping, combos disabled, expired,
 *    usage limit hit, etc.) — that used to fail completely silently.
 * 2. #ensureGiftInCart now checks addRes.ok before trying to apply the
 *    discount, so it doesn't try to discount a gift that was never added.
 * 3. Failures now console.warn with the actual reason where possible, and
 *    dispatch a `bt:gift-discount-error` CustomEvent so the merchant/theme
 *    can hook in a visible UI warning if desired.
 */
class BtSubscriptionWidget extends HTMLElement {
  #data = null;
  #variantId = '';
  #addingGift = false;
  #expectGiftOnNextAdd = false;

  connectedCallback() {
    const json = this.querySelector('[data-bt-sub-data]');
    try {
      this.#data = JSON.parse(json?.textContent || '{}');
    } catch {
      this.#data = {};
    }
    this.#variantId = String(this.dataset.variantId || '');

    this.addEventListener('change', this.#onChange);
    this.querySelector('[data-bt-gift-open]')?.addEventListener('click', this.#openModal);
    this.querySelector('[data-bt-gift-close]')?.addEventListener('click', this.#closeModal);
    this.querySelector('[data-bt-gift-modal]')?.addEventListener('click', this.#onDialogClick);
    document.addEventListener('variant:update', this.#onVariantUpdate);
    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('submit', this.#onFormSubmitCapture, true);
    document.addEventListener('keydown', this.#onKeydown);

    queueMicrotask(() => {
      this.#paintSelected();
      this.#syncForm();
      this.#syncGiftVisibility();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.querySelector('[data-bt-gift-open]')?.removeEventListener('click', this.#openModal);
    this.querySelector('[data-bt-gift-close]')?.removeEventListener('click', this.#closeModal);
    this.querySelector('[data-bt-gift-modal]')?.removeEventListener('click', this.#onDialogClick);
    document.removeEventListener('variant:update', this.#onVariantUpdate);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    document.removeEventListener('submit', this.#onFormSubmitCapture, true);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  #onChange = () => {
    this.#paintSelected();
    this.#syncForm();
    this.#syncGiftVisibility();
  };

  #onVariantUpdate = (event) => {
    const productId = event?.detail?.data?.productId;
    if (productId && String(productId) !== String(this.dataset.productId)) return;
    const id = event?.detail?.resource?.id;
    if (!id) return;
    // Ignore variants that aren't part of this PDP product's price map
    // (e.g. theme-editor gift product context must never drive widget prices).
    if (!this.#data?.[String(id)]) return;
    this.#variantId = String(id);
    this.dataset.variantId = this.#variantId;
    this.#refreshPrices();
    this.#syncForm();
  };

  /**
   * Do NOT preventDefault — only sync selling_plan and mark gift for after ATC.
   */
  #onFormSubmitCapture = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    const productForm = form.closest('product-form-component');
    if (!productForm) return;
    if (String(productForm.dataset.productId) !== String(this.dataset.productId)) return;

    this.#syncForm();

    const planInput = form.querySelector('input[name="selling_plan"]');
    const hasPlan = !!(planInput && String(planInput.value || '').trim());
    const giftReady =
      this.dataset.giftEnabled === 'true' &&
      !!(this.dataset.giftVariantId || this.querySelector('[data-bt-gift-banner]')?.getAttribute('data-gift-variant-id'));

    this.#expectGiftOnNextAdd = hasPlan && giftReady;
  };

  #onCartUpdate = (event) => {
    if (this.#addingGift) return;

    const data = event?.detail?.data || {};
    const source = data.source;

    if (source === 'bt-subscription-gift' || source === 'bt-buy-once') return;
    if (source !== 'product-form-component') return;
    if (data.didError) {
      this.#expectGiftOnNextAdd = false;
      return;
    }

    const productId = data.productId;
    if (productId && String(productId) !== String(this.dataset.productId)) return;

    const shouldAddGift = this.#expectGiftOnNextAdd || this.#isSubscriptionSelected();
    this.#expectGiftOnNextAdd = false;
    if (!shouldAddGift) return;
    if (this.dataset.giftEnabled !== 'true' && !this.querySelector('[data-bt-gift-banner]')) return;

    setTimeout(() => this.#ensureGiftInCart(), 200);
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#closeModal();
  };

  #onDialogClick = (event) => {
    const dialog = this.querySelector('[data-bt-gift-modal]');
    if (event.target === dialog) this.#closeModal();
  };

  #openModal = (event) => {
    event.preventDefault();
    const dialog = this.querySelector('[data-bt-gift-modal]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('bt-sub-gift-modal-open');
  };

  #closeModal = () => {
    const dialog = this.querySelector('[data-bt-gift-modal]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.documentElement.classList.remove('bt-sub-gift-modal-open');
  };

  #paintSelected() {
    this.querySelectorAll('[data-bt-sub-option]').forEach((option) => {
      const input = option.querySelector('input[data-bt-plan]');
      option.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  #isSubscriptionSelected() {
    const selected = this.querySelector('input[data-bt-plan]:checked');
    return !!(selected && selected.value);
  }

  #syncGiftVisibility() {
    const banner = this.querySelector('[data-bt-gift-banner]');
    if (!(banner instanceof HTMLElement)) return;
    banner.hidden = !this.#isSubscriptionSelected();
  }

  #getForm() {
    const root = this.closest('.shopify-section') || document;
    return (
      root.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"] form[data-type="add-to-cart-form"]`
      ) || document.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
  }

  #syncForm() {
    const form = this.#getForm();
    if (!form) return;

    const selected = this.querySelector('input[data-bt-plan]:checked');
    const planId = selected ? selected.value : '';
    let hidden = form.querySelector('input[name="selling_plan"][data-bt-sub]');

    if (planId) {
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'selling_plan';
        hidden.setAttribute('data-bt-sub', 'true');
        form.appendChild(hidden);
      }
      hidden.value = planId;
    } else if (hidden) {
      hidden.remove();
    }

    this.#syncQuantity(selected);
  }

  /**
   * Match ATC quantity to the selected plan duration (1 month → 1, 3 months → 3).
   * PDP quantity selector is disabled, so we keep a hidden quantity field on the form.
   */
  #syncQuantity(selected = this.querySelector('input[data-bt-plan]:checked')) {
    const form = this.#getForm();
    if (!form) return;

    const qtyAttr = selected?.getAttribute('data-bt-qty');
    const qty = Math.max(1, parseInt(qtyAttr || '1', 10) || 1);

    let quantityInput =
      form.querySelector('input[name="quantity"][data-bt-sub-qty]') ||
      form.querySelector('input[name="quantity"]');

    if (!(quantityInput instanceof HTMLInputElement)) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
      quantityInput.setAttribute('data-bt-sub-qty', 'true');
      form.appendChild(quantityInput);
    }

    quantityInput.value = String(qty);

    const productForm = form.closest('product-form-component');
    if (productForm instanceof HTMLElement) {
      productForm.dataset.quantityDefault = String(qty);
    }

    const quantitySelector = productForm?.querySelector('quantity-selector-component');
    if (quantitySelector && typeof quantitySelector.setValue === 'function') {
      quantitySelector.setValue(String(qty));
    } else if (quantityInput !== form.querySelector('input[name="quantity"][data-bt-sub-qty]')) {
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #refreshPrices() {
    const variant = this.#data?.[this.#variantId];
    if (!variant) return;

    this.querySelectorAll('[data-bt-price]').forEach((el) => {
      const planId = el.getAttribute('data-bt-price');
      el.textContent = variant.plans?.[planId]?.price_formatted || variant.price_formatted || '';
    });

    this.querySelectorAll('[data-bt-compare]').forEach((el) => {
      const planId = el.getAttribute('data-bt-compare');
      const compare = variant.plans?.[planId]?.compare_at_price_formatted;
      el.innerHTML = compare
        ? `<span class="bt-sub__compare-value"><span>${compare}</span><sup>†</sup></span>`
        : '';
    });

    const onePrice = this.querySelector('[data-bt-onetime-price]');
    if (onePrice) onePrice.textContent = variant.price_formatted || '';

    const oneCompare = this.querySelector('[data-bt-onetime-compare]');
    if (oneCompare) {
      oneCompare.innerHTML = variant.compare_at_price_formatted
        ? `<span class="bt-sub__compare-value"><span>${variant.compare_at_price_formatted}</span><sup>†</sup></span>`
        : '';
    }
  }

  #cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  #giftVariantId() {
    return (
      this.dataset.giftVariantId ||
      this.querySelector('[data-bt-gift-banner]')?.getAttribute('data-gift-variant-id') ||
      ''
    );
  }

  #cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component, cart-discount-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    });
    return [...new Set(ids)];
  }

  /**
   * Reports whether `code` is present AND applicable in a cart.js-shaped
   * response. Logs a clear console.warn and fires a bt:gift-discount-error
   * event when it isn't, instead of failing silently.
   */
  #reportDiscountStatus(cart, code) {
    const entries = cart?.discount_codes || [];
    const entry = entries.find((e) => String(e?.code || '').toLowerCase() === code.toLowerCase());

    if (!entry) {
      console.warn(
        `[BT Subscription] Discount code "${code}" is not present in cart.discount_codes after update — the /cart/update.js call may not have been accepted by this theme/cart drawer.`
      );
      this.#dispatchGiftDiscountError(code, 'missing');
      return false;
    }

    if (entry.applicable === false) {
      console.warn(
        `[BT Subscription] Discount code "${code}" was rejected by Shopify (applicable: false). ` +
          `Check in Admin → Discounts that it: is active, is scoped to the gift product/variant, ` +
          `allows combining with product/order discounts, has no unmet minimum requirements, ` +
          `and hasn't hit its usage limit for this customer.`
      );
      this.#dispatchGiftDiscountError(code, 'not_applicable');
      return false;
    }

    return true;
  }

  #dispatchGiftDiscountError(code, reason) {
    document.dispatchEvent(
      new CustomEvent('bt:gift-discount-error', {
        bubbles: true,
        detail: { code, reason },
      })
    );
  }

  async #applyGiftDiscount(discountCode) {
    const code = String(discountCode || '').trim();
    if (!code) return null;

    const root = this.#cartRoot();
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    const already = (cart.discount_codes || []).some(
      (entry) => String(entry?.code || '').toLowerCase() === code.toLowerCase() && entry?.applicable !== false
    );
    if (already) return cart;

    const existing = (cart.discount_codes || [])
      .filter((entry) => entry?.applicable !== false && entry?.code)
      .map((entry) => entry.code);
    if (!existing.map((c) => String(c).toLowerCase()).includes(code.toLowerCase())) {
      existing.push(code);
    }

    const sections = this.#cartSectionIds();
    const updateUrl = window.Theme?.routes?.cart_update_url || `${root}cart/update.js`;
    const updateRes = await fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: existing.join(','),
        ...(sections.length ? { sections } : {}),
      }),
    });

    if (!updateRes.ok) {
      console.warn('[BT Subscription] Discount update request failed', await updateRes.text());
      this.#dispatchGiftDiscountError(code, 'request_failed');
      return null;
    }

    const updatedCart = await updateRes.json();
    // FIX: previously we returned here without ever checking whether Shopify
    // actually accepted/applied the code. A 200 OK does not mean applicable.
    this.#reportDiscountStatus(updatedCart, code);
    return updatedCart;
  }

  async #ensureGiftInCart() {
    const giftVariantId = this.#giftVariantId();
    const discountCode = (this.dataset.discountCode || 'free').trim();
    if (!giftVariantId || this.#addingGift) return;

    this.#addingGift = true;
    try {
      const root = this.#cartRoot();
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      const hasGift = (cart.items || []).some(
        (item) =>
          String(item.variant_id) === String(giftVariantId) ||
          item.properties?._bt_free_gift === 'true'
      );

      let giftInCart = hasGift;

      if (!hasGift) {
        const addRes = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(giftVariantId),
                quantity: 1,
                properties: {
                  _bt_free_gift: 'true',
                },
              },
            ],
          }),
        });

        if (!addRes.ok) {
          // FIX: previously we ignored this failure and still tried to apply
          // the discount to a gift that was never added to the cart.
          console.warn('[BT Subscription] Could not add free gift', await addRes.text());
        } else {
          giftInCart = true;
        }
      }

      let updatedCart = null;
      if (giftInCart && discountCode) {
        updatedCart = await this.#applyGiftDiscount(discountCode);
      } else if (!giftInCart) {
        console.warn('[BT Subscription] Skipping discount apply — gift item was not added to cart.');
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: updatedCart || null,
            data: {
              source: 'bt-subscription-gift',
              sections: updatedCart?.sections,
            },
          },
        })
      );
    } catch (err) {
      console.warn('[BT Subscription] Free gift sync failed', err);
    } finally {
      setTimeout(() => {
        this.#addingGift = false;
      }, 800);
    }
  }
}

if (!customElements.get('bt-subscription-widget')) {
  customElements.define('bt-subscription-widget', BtSubscriptionWidget);
}